import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ColumnWidth, TableName } from 'modules/core/enums';
import {
  useDebounceCallback,
  useDebounceVariable,
  useFeesAndTaxesInitialValues,
  useFormEnhanced,
  useModalState,
  useSettingsInitialValues,
  useHiddenScrollBar,
} from 'modules/core/hooks';
import { FormProvider } from 'react-hook-form';
import {
  getIsTableView,
  getLoanVariablesCurrentVehicle,
  getNotAvailableVehicles,
  getNotFoundVehicles,
  getAvailableFilters,
  getRACInitialValues,
  getRACReportData,
  getRateSheetsDataFlags,
  getVehicleFetchingFlags,
  getVehiclesByParams,
  getVehiclesByStock,
  getTiers,
  getRACInitialErrors,
} from 'store/selectors/RACAffordabilityEngine.selector';
import {
  clearLoanVariablesCurrentVehicle,
  closeRateSheetsModal,
  RACAffordabilityEngineSlice,
  resetByStock,
  resetVehicles,
  resetVehiclesByParams,
  resetVehiclesByStock,
  resetResetCount,
  setIsTableView,
  setDealSourceTierEditable,
  setFirstDealSourceRender,
} from 'store/reducers/RACAffordabilityEngine.reducer';
import { VehicleSearchTable } from 'modules/VehicleSearchTable';
import { useTablePlaceholderType } from 'modules/VehicleSearchTable/hooks';
import { getRACVehiclesByStockThunk, getRACVehiclesThunk, getRateSheetsDataThunk } from 'actions/vehicleActions';
import { useSearchTracking } from 'modules/core/hooks/useSearchTracking';
import { AppDispatch } from 'store/store';
import { IRACVehicle } from 'modules/core/types';
import { useSwitchFilters } from 'modules/core/hooks/useSwitchFilters';
import { LenderVehicleCard } from 'modules/core/components/Table/VehicleCardView/LenderVehicleCard/LenderVehicleCard';
import { vehicleApi } from 'api';
import { FilterView } from 'modules/core/enums/filterView.enum';
import { getSubmitVehicleObjectQuery, getTotalVehicles, userHasInventory } from 'modules/core/utils';
import {
  RAC_BY_STOCK_FORM_NAMES_MAPPER,
  RAC_FORM_NAMES_MAPPER,
} from 'modules/core/constants/mappers/submitVehicles.mapper';
import { IS_VALID_DELAY, MODAL_DIALOG_ID, REQUEST_DELAY, STATIC_IMAGE_URLS } from 'modules/core/constants';
import { getFrontAndBackEndsAdds } from 'components/Modals/FrontAndBackEndsAddsModal/service';
import { getFeesAndTaxesState } from 'store/selectors/controls.selector';
import { getUser } from 'store/selectors/auth.selector';
import { getRegionControlsThunk } from 'actions/controlActions';
import { SetupRateSheetAndTaxRateModal } from 'components/Modals/SetupRateSheetAndTaxRateModal/SetupRateSheetAndTaxRateModal';
import { FEES_AND_TAXES_FIELD_NAMES, FEES_AND_TAXES_INITIAL_VALUES } from 'modules/core/shared';
import { IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG } from 'modules/auth/constants/auth.const';
import { useConfirmTabClose } from 'hooks/useConfirmTabClose';
import {
  getAddUserVehicleFlags,
  getUpdateUserVehicleFlags,
  getCreatedUserVehicle,
  getCreatedUserVehicleTableName,
  getIsDealSourceVehicle,
} from 'store/selectors/vehicle.selector';
import { setIsModalsDisabled } from 'store/reducers/ui.reducer';
import {
  setAddedIsUserVehicle,
  resetAddedIsUserVehicle,
  setIsDealSourceVehicle,
  resetIsDealSourceVehicle,
} from 'store/reducers/vehicle.reducer';
import { AddVehicleDialog } from 'components/Modals/AddVehicleDialog/AddVehicleDialog';
import { TitleContainer } from 'modules/core/components/PageHeader/TitleContainer/TitleContainer';
import { getIsModalsDisabled } from 'store/selectors/ui.selector';
import { getRouteOneDealersThunk } from 'actions/dealerActions';
import { useDealSourceData } from 'hooks/useDealSourceData';

import {
  RAC_CONSUMER_INFORMATION_FIRST_FIELDS,
  RAC_CONSUMER_INFORMATION_SECOND_FIELDS,
} from './const/fieldConfig.const';
import { IRACAffordabilityEngineForm } from './types/RACAffordabilityEngineForm';
import { APPROVED_LOAN_TERMS_NAMES_WATCH, DEFAULT_PLACEHOLDER_TITLE } from './RACAffordabilityEngine.const';
import { RACAffordabilityEngineService } from './RACAffordabilityEngine.service';
import { AdaptedCol } from '../ProbabilityEstimator/styles';
import { ConsumerInformation } from '../ProbabilityEstimator/ConsumerInformation';
import { PageName } from '../ProbabilityEstimator/ConsumerInformation/hooks/useConsumerInformationData';
import { APPROVED_LOAN_TERMS_FIELD_NAMES, WITHOUT_INVENTORY_FIELDS_NAMES } from './const/fieldNames.const';
import { useDealSourceValues } from './hooks/useDealSourceValues';
import { useHideRACVehicleField } from '../core/hooks/useHideVehicleField';
import { DisabledModals } from '../core/enums/disabledModals';
import { RACLoanVariablesDialog } from './RACLoanVariablesDialog';

export const RACAffordabilityEngine: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { dealSourceData, dealSourceDataFlags, firstDealSourceRender, isDealSourceDataEmpty } = useDealSourceData(
    TableName.RAC,
  );
  const user = useSelector(getUser);
  const tiers = useSelector(getTiers);
  const vehiclesByParams = useSelector(getVehiclesByParams);
  const vehiclesByStock = useSelector(getVehiclesByStock);
  const availableFilters = useSelector(getAvailableFilters);
  const notAvailableVehicles = useSelector(getNotAvailableVehicles);
  const notFoundVehicles = useSelector(getNotFoundVehicles);
  const isTableView = useSelector(getIsTableView);
  const addVehicleFlags = useSelector(getAddUserVehicleFlags);
  const updateVehicleFlags = useSelector(getUpdateUserVehicleFlags);
  const createdVehicle = useSelector(getCreatedUserVehicle);
  const { isShow } = useSelector(getRateSheetsDataFlags);
  const currentVehicleData = useSelector(getLoanVariablesCurrentVehicle);
  const createdUserVehicleTableName = useSelector(getCreatedUserVehicleTableName);
  const { resetCount, isFetchingVehiclesFailed, isFetchingVehicles } = useSelector(
    getVehicleFetchingFlags,
    shallowEqual,
  );
  const isDealSourceVehicle = useSelector(getIsDealSourceVehicle);
  const [showAddVehicleDialog, setShowAddVehicleDialog] = useState<boolean>(false);
  const [showCreatedVehicleErrorMessage, setShowCreatedVehicleErrorMessage] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [addedVehicleId, setAddedVehicleId] = useState<number>(0);
  const isSsoAndLoanVariablesDialogOpen = sessionStorage.getItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG);
  const { handleOpenDialog, handleCloseDialog, isModalDialogActive } = useModalState([MODAL_DIALOG_ID.LOAN_VARIABLES]);
  const isModalsDisabled = useSelector(getIsModalsDisabled);
  const isMakeItWork = !!user?.is_sso && dealSourceData?.lender_path === window.location.pathname;
  const isWholesaleInventory = user?.withWholesaleInventory;

  const { methods, syncFormAction } = useFormEnhanced<IRACAffordabilityEngineForm>(
    RACAffordabilityEngineSlice.actions.syncInitialValues,
    RACAffordabilityEngineSlice.actions.syncInitialErrors,
    getRACInitialValues,
    getRACInitialErrors,
    {
      mode: 'onChange',
      shouldUnregister: false,
    },
  );

  useHiddenScrollBar(isModalDialogActive);

  useEffect(() => {
    const params = isMakeItWork ? { deal_date: dealSourceData?.submitted_date_time } : {};

    dispatch(getRegionControlsThunk(params));
  }, [dispatch, dealSourceData?.submitted_date_time, isMakeItWork]);

  const {
    setValue,
    watch,
    getValues,
    formState: { isValid, errors },
  } = methods;

  const isFormValid = Object.keys(errors).length === 0 && !isValid ? true : isValid;

  const {
    tier,
    desiredMonthlyPayment,
    grossMonthlyIncome,
    netDownPayment,
    feesAndTaxes,
    stocks,
    frontAndBackEndsAdds,
    rateSheetSettingsModal,
    ...otherValues
  } = watch(APPROVED_LOAN_TERMS_NAMES_WATCH) as IRACAffordabilityEngineForm;
  const { tableView, filterView, preferences, totalNetDownPaymentDialog, totalFrontAndBackEndsAddsDialog } = watch();

  const formValues = watch() as IRACAffordabilityEngineForm;
  const { insuranceControl } = useSelector(getFeesAndTaxesState);
  const { addOns, vsc, gap } = formValues.totalFrontAndBackEndsAddsDialog;

  useEffect(() => {
    if (insuranceControl !== 0 && !frontAndBackEndsAdds) {
      const frontAndBackEndsAdds = getFrontAndBackEndsAdds(addOns || 0, vsc || 0, gap || 0);

      setValue(APPROVED_LOAN_TERMS_FIELD_NAMES.FRONT_AND_BACK_ENDS_ADDS, frontAndBackEndsAdds || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insuranceControl, setValue, frontAndBackEndsAdds, vsc]);

  const commonDependencies = [
    tier.id,
    desiredMonthlyPayment,
    grossMonthlyIncome,
    ...Object.values(feesAndTaxes),
    ...Object.values(preferences),
    totalFrontAndBackEndsAddsDialog.addOns,
    totalFrontAndBackEndsAddsDialog.gap,
    totalFrontAndBackEndsAddsDialog.vsc,
    totalNetDownPaymentDialog.cashDown,
    totalNetDownPaymentDialog.tradeValue,
    totalNetDownPaymentDialog.amountOwed,
  ];

  const { currentFilterName, isValidStock, isRequestNeeded } = useSwitchFilters(
    commonDependencies,
    filterView,
    isFetchingVehiclesFailed,
    watch,
    setValue,
  );

  useEffect(() => {
    if (isRequestNeeded) {
      if (filterView === FilterView.BY_PARAMS) dispatch(resetVehiclesByParams());
      else dispatch(resetVehiclesByStock());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterView]);

  useSearchTracking({
    resetCount,
    isFetchingVehicles,
    isFetchingVehiclesFailed,
    isRac: true,
    lender: dealSourceData?.lender ? { type: 'route_one_id', id: dealSourceData.lender } : null,
  });

  useEffect(() => {
    if (!isFetchingVehicles) setIsFetching(false);
  }, [isFetchingVehicles]);

  const vehiclesData: IRACVehicle[] | undefined =
    filterView === FilterView.BY_PARAMS ? vehiclesByParams : vehiclesByStock;

  const vehicles = useHideRACVehicleField(vehiclesData, isMakeItWork ? dealSourceData?.vehicle_id : undefined);
  const totalVehicles = getTotalVehicles(filterView, isValidStock, vehicles);

  useEffect(() => {
    if (!isValidStock) dispatch(resetByStock());
  }, [dispatch, isValidStock]);

  useEffect(() => {
    if (!stocks.length && vehicles?.length) dispatch(resetByStock());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, vehicles?.length]);

  const onSubmit = useCallback(
    ({ dealSourceData, isMakeItWork, currentFilterName }) => {
      const values = getValues() as IRACAffordabilityEngineForm;
      const isFilterByParams = values.filterView === FilterView.BY_PARAMS;

      const CURRENT_RAC_FORM_NAMES_MAPPER = isFilterByParams ? RAC_FORM_NAMES_MAPPER : RAC_BY_STOCK_FORM_NAMES_MAPPER;

      if (typeof values.vehicleFilters.vehicleType === 'string') {
        values.condition = values.vehicleFilters.vehicleType;
      }

      // eslint-disable-next-line no-param-reassign
      if (!isFilterByParams) values.vehicleFilters.minMonthlyPayment = 0;

      let vehicleParameters = {
        ...getSubmitVehicleObjectQuery<IRACAffordabilityEngineForm>(
          values,
          RACAffordabilityEngineService.getSubmitQuery,
          CURRENT_RAC_FORM_NAMES_MAPPER,
        ),
        ...(isMakeItWork && { deal_date: dealSourceData?.submitted_date_time }),
      };

      syncFormAction();
      setValue(currentFilterName, false);

      if (isFilterByParams) dispatch(getRACVehiclesThunk(vehicleParameters));
      else dispatch(getRACVehiclesByStockThunk(vehicleParameters));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, syncFormAction, getValues, setValue],
  );

  useSettingsInitialValues(setValue, getRACInitialValues);
  useFeesAndTaxesInitialValues(
    setValue,
    getRACInitialValues,
    vsc,
    isMakeItWork,
    user?.isWithInventory,
    false,
    formValues.routeOneDealer,
  );

  const tierOption = tiers.find((tier) => tier.tier === parseInt(dealSourceData?.finance_details?.tier));

  useDealSourceValues(
    setValue,
    isDealSourceDataEmpty,
    dealSourceData,
    resetCount,
    tiers.length === 0,
    tier.max_allowed_pti,
    tier.max_allowed_payment,
    firstDealSourceRender,
    setDealSourceTierEditable,
    setFirstDealSourceRender,
    !!tierOption,
    tierOption,
    tiers,
    isMakeItWork,
    user?.isWithInventory,
    true,
  );

  const previewPlaceholder = (
    <Trans
      defaults={t('components.RACAffordabilityEngine.previewPlaceholderNoResults')}
      components={[<br />]}
      style={{ maxWidth: '700px' }}
    />
  );

  const { debounced: onSubmitDebounced, cancel: cancelSubmitDebounced } = useDebounceCallback((data) => {
    onSubmit(data);
  }, REQUEST_DELAY);

  const handleSearchSubmit = useCallback(() => {
    if (isValid) onSubmit({ dealSourceData, isMakeItWork, currentFilterName });
  }, [isValid, onSubmit, dealSourceData, isMakeItWork, currentFilterName]);

  // useDebounceVariable hook is used to fix isValid variable state behavior
  const debouncedIsValid = useDebounceVariable(isFormValid, IS_VALID_DELAY);

  const areRequiredFieldsFilledIn = Boolean(
    grossMonthlyIncome && tier.tier !== 0 && !Number.isNaN(feesAndTaxes.salesTaxRate),
  );

  const tablePlaceholderType = useTablePlaceholderType(
    areRequiredFieldsFilledIn,
    vehicles || [],
    getVehicleFetchingFlags,
    isValidStock,
    isFetching,
  );

  const handleChangeCallback = useCallback(() => {
    if (!!resetCount && !areRequiredFieldsFilledIn) return;

    if (
      debouncedIsValid &&
      areRequiredFieldsFilledIn &&
      (isRequestNeeded || addVehicleFlags.isLoaded || updateVehicleFlags.isLoaded) &&
      isValidStock
    ) {
      setIsFetching(true);

      vehicleApi.cancelGetRACVehiclesRequests();
      vehicleApi.cancelGetRACVehiclesByStockRequest();
      onSubmitDebounced({ dealSourceData, isMakeItWork, currentFilterName });

      if (addVehicleFlags.isLoaded) setShowCreatedVehicleErrorMessage(true);
    } else if (!areRequiredFieldsFilledIn || !isValidStock || !debouncedIsValid) {
      setIsFetching(false);
      cancelSubmitDebounced();
    }
  }, [
    addVehicleFlags.isLoaded,
    updateVehicleFlags.isLoaded,
    currentFilterName,
    isRequestNeeded,
    isValidStock,
    onSubmitDebounced,
    debouncedIsValid,
    areRequiredFieldsFilledIn,
    resetCount,
    dealSourceData,
    isMakeItWork,
    cancelSubmitDebounced,
  ]);

  useEffect(() => {
    handleChangeCallback();
  }, [
    tableView,
    tier.tier,
    desiredMonthlyPayment,
    grossMonthlyIncome,
    feesAndTaxes.vscAndGapInsurance,
    feesAndTaxes.estimatedFees,
    feesAndTaxes.documentFee,
    feesAndTaxes.salesTaxRate,
    stocks.length,
    handleChangeCallback,
    // Inserting the otherValues object initiates a comparison by reference and an additional hook launch.
    // This technique is used to avoid spreading the entire otherValues object separately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ...Object.values(otherValues),
  ]);

  useEffect(() => {
    if (areRequiredFieldsFilledIn) {
      dispatch(resetResetCount());
    }
  }, [dispatch, areRequiredFieldsFilledIn]);

  useEffect(() => {
    if (!areRequiredFieldsFilledIn && typeof totalVehicles !== 'undefined' && !isWholesaleInventory) {
      dispatch(resetVehicles());
    }
  }, [dispatch, areRequiredFieldsFilledIn, totalVehicles, isWholesaleInventory]);

  useEffect(() => {
    if (addVehicleFlags.isFetching) setShowCreatedVehicleErrorMessage(false);
  }, [addVehicleFlags.isFetching]);

  useEffect(() => {
    if (!areRequiredFieldsFilledIn) {
      vehicleApi.cancelGetRACVehiclesRequests();
      vehicleApi.cancelGetRACVehiclesByStockRequest();
    }
  }, [areRequiredFieldsFilledIn]);

  useConfirmTabClose({ canShow: user?.is_sso });

  const isDealSourceVehicleInList = useMemo(
    () => !!vehicles?.length && vehicles.some((vehicle) => vehicle.id === dealSourceData?.vehicle_id),
    [vehicles, dealSourceData?.vehicle_id],
  );

  const isCreatedVehicleInList = useMemo(
    () => !!vehicles?.length && vehicles.some((vehicle) => vehicle.id === createdVehicle.id),
    [vehicles, createdVehicle.id],
  );

  const isDealSourceVehicleIncomplete = useMemo(
    () =>
      dealSourceData?.sale_vehicle &&
      dealSourceData?.manually_created &&
      (!dealSourceData.sale_vehicle?.retail_price || !dealSourceData?.sale_vehicle?.book_value),
    [dealSourceData?.sale_vehicle, dealSourceData?.manually_created],
  );

  useEffect(() => {
    if (
      user?.is_sso &&
      (user?.isWithInventory ? !isSsoAndLoanVariablesDialogOpen : dealSourceDataFlags.isReceived) &&
      !isDealSourceDataEmpty &&
      dealSourceData.vehicle_exists &&
      tier?.tier &&
      !isShow &&
      (user?.isWithInventory || user?.withWholesaleInventory) &&
      (isDealSourceVehicleInList || isDealSourceVehicleIncomplete) &&
      ((!user?.isWithInventory && !isModalsDisabled[DisabledModals.REHASH_CALCULATOR]) || user?.isWithInventory)
    ) {
      dispatch(setIsDealSourceVehicle(true));
      handleOpenDialog();

      sessionStorage.setItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG, 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDealSourceDataEmpty,
    dealSourceData.vehicle_exists,
    user?.is_sso,
    tier?.tier,
    user?.isWithInventory,
    user?.withWholesaleInventory,
    isSsoAndLoanVariablesDialogOpen,
    isShow,
    isDealSourceVehicleInList,
    isDealSourceVehicleIncomplete,
  ]);

  useEffect(() => {
    if (
      user?.is_sso &&
      !user?.isWithInventory &&
      !user?.withWholesaleInventory &&
      !isModalsDisabled[DisabledModals.RAC] &&
      !isModalsDisabled[DisabledModals.REHASH_CALCULATOR] &&
      !isDealSourceDataEmpty &&
      !isShow &&
      tier?.tier
    ) {
      if (dealSourceData.vehicle_exists && dealSourceDataFlags.isReceived) {
        dispatch(setIsDealSourceVehicle(true));
        handleOpenDialog();
      } else if (!isSsoAndLoanVariablesDialogOpen) {
        setShowAddVehicleDialog(true);
      }

      sessionStorage.setItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG, 'true');
    }
  }, [
    isDealSourceDataEmpty,
    dealSourceData.vehicle_exists,
    user?.is_sso,
    tier?.tier,
    user?.isWithInventory,
    user?.withWholesaleInventory,
    isSsoAndLoanVariablesDialogOpen,
    dealSourceDataFlags.isReceived,
    isModalsDisabled,
    isShow,
    handleOpenDialog,
    dispatch,
  ]);

  useEffect(() => {
    if (
      !addVehicleFlags.isLoaded &&
      createdVehicle.id &&
      !currentVehicleData?.id &&
      createdUserVehicleTableName === TableName.RAC &&
      isCreatedVehicleInList
    ) {
      dispatch(setAddedIsUserVehicle(true));
      handleOpenDialog();
    }

    if (addVehicleFlags.isLoaded && createdVehicle.id) {
      dispatch(clearLoanVariablesCurrentVehicle());
      handleCloseDialog()();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addVehicleFlags.isLoaded, createdVehicle.id, dispatch, currentVehicleData?.id, isCreatedVehicleInList]);

  useEffect(() => {
    const isDealSourceDataExists = Object.keys(dealSourceData).length > 0;

    if (user?.is_sso && !user?.isWithInventory && isDealSourceDataExists && firstDealSourceRender) {
      dispatch(getRateSheetsDataThunk());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, Object.keys(dealSourceData).length, firstDealSourceRender]);

  useEffect(() => {
    if (user?.is_sso && !user?.isWithInventory && dealSourceDataFlags.isReceived && isDealSourceDataEmpty) {
      dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.UNLOCK_INVENTORY }));
    }

    if (user?.is_sso && dealSourceDataFlags.isReceived && !isDealSourceDataEmpty && user?.isWithInventory) {
      dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));
    }
  }, [user, isDealSourceDataEmpty, dealSourceDataFlags.isReceived, dispatch]);

  useEffect(() => {
    if (!areRequiredFieldsFilledIn) dispatch(setIsModalsDisabled({ value: true, key: DisabledModals.RAC }));
    else dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.RAC }));
  }, [areRequiredFieldsFilledIn, dispatch, user?.isWithInventory]);

  useEffect(() => {
    if (!user?.isWithInventory && feesAndTaxes.salesTaxRate === FEES_AND_TAXES_INITIAL_VALUES.salesTaxRate) {
      setValue(FEES_AND_TAXES_FIELD_NAMES.SALES_TAX_RATE, user?.rateSheetsData?.tax_rate);
    }
  }, [setValue, user?.isWithInventory, user?.rateSheetsData?.tax_rate, feesAndTaxes.salesTaxRate]);

  useEffect(() => {
    if (createdVehicle.id) setAddedVehicleId(createdVehicle.id);
  }, [createdVehicle.id]);

  useEffect(() => {
    if (!user?.isWithInventory) {
      setValue(WITHOUT_INVENTORY_FIELDS_NAMES.RATE_SHEET, user?.rateSheetsData?.rate_sheet || null);
      setValue(WITHOUT_INVENTORY_FIELDS_NAMES.REGION, user?.rateSheetsData?.region || null);
    }
  }, [setValue, user?.isWithInventory, user?.rateSheetsData?.rate_sheet, user?.rateSheetsData?.region]);

  useEffect(() => {
    dispatch(getRouteOneDealersThunk(false));
  }, [dispatch]);

  const title = <TitleContainer imgSrc={STATIC_IMAGE_URLS.racLogo} style={{ marginLeft: '-5px' }} />;

  return (
    <FormProvider {...methods}>
      <Form
        onSubmit={(event) => {
          event.preventDefault();
          handleSearchSubmit();
        }}
      >
        <Row>
          <AdaptedCol xs={ColumnWidth.FULL}>
            <ConsumerInformation
              title={title}
              totalVehicles={totalVehicles}
              firstConsumerFields={RAC_CONSUMER_INFORMATION_FIRST_FIELDS}
              secondConsumerFields={RAC_CONSUMER_INFORMATION_SECOND_FIELDS}
              isVehicleTableLoading={isFetchingVehicles}
              pageName={PageName.RAC}
              submitCallback={handleChangeCallback}
              cancelSubmitDebounced={cancelSubmitDebounced}
              areRequiredFieldsFilledIn={areRequiredFieldsFilledIn}
              fetchingVehicleFlagsSelector={getVehicleFetchingFlags}
              tableName={TableName.RAC}
              isMakeItWork={isMakeItWork}
              isDealSourceDataEmpty={isDealSourceDataEmpty}
              isRouteOneFlow={isMakeItWork}
            />
          </AdaptedCol>
        </Row>
        <Row>
          <Col xs={ColumnWidth.FULL}>
            <VehicleSearchTable
              tableName={TableName.RAC}
              tablePlaceholderType={tablePlaceholderType}
              data={vehicles || []}
              tableSubmitCallback={handleSearchSubmit}
              defaultPlaceholderTitle={DEFAULT_PLACEHOLDER_TITLE}
              previewPlaceholder={previewPlaceholder}
              isTableFiltersDisabled={!areRequiredFieldsFilledIn || !userHasInventory(user)}
              isTableActionsDisabled={!vehicles?.length || !areRequiredFieldsFilledIn}
              isTableView={isTableView}
              setIsTableView={setIsTableView}
              fetchingVehicleFlagsSelector={getVehicleFetchingFlags}
              areRequiredFieldsFilledIn={areRequiredFieldsFilledIn}
              CustomVehicleCard={LenderVehicleCard}
              reportDataSelector={getRACReportData}
              notAvailableVehicles={notAvailableVehicles}
              notFoundVehicles={notFoundVehicles}
              isFilterByStockNumber
              isRac
              availableFilters={availableFilters}
              isMakeItWork={user?.is_sso}
              dealSourceData={dealSourceData}
              tier={tier.tier}
              skipAutoStateReset={!resetCount}
              showNoVehicleErrorMessage={
                isMakeItWork &&
                formValues.filterView === FilterView.BY_PARAMS &&
                !isDealSourceDataEmpty &&
                !!vehicles?.length &&
                !dealSourceData.vehicle_exists
              }
              showNoVehicleInInventoryErrorMessage={
                isMakeItWork &&
                formValues.filterView === FilterView.BY_PARAMS &&
                !isDealSourceDataEmpty &&
                !!vehicles?.length &&
                dealSourceData.vehicle_exists &&
                !vehicles.map((vehicle) => vehicle.id).includes(dealSourceData.vehicle_id)
              }
              showNoAddedVehicleErrorMessage={
                !!vehicles?.length &&
                createdUserVehicleTableName === TableName.RAC &&
                showCreatedVehicleErrorMessage &&
                !isFetching &&
                !vehicles.map((vehicle) => vehicle.id).includes(addedVehicleId)
              }
              showNoResultsAndDealSourceVehicle={
                isMakeItWork &&
                formValues.filterView === FilterView.BY_PARAMS &&
                !isDealSourceDataEmpty &&
                !!vehicles &&
                !vehicles?.length &&
                dealSourceData.vehicle_exists
              }
            />
          </Col>
        </Row>
      </Form>
      {isModalDialogActive && (
        <RACLoanVariablesDialog
          isLoanVariablesDialogActive={isModalDialogActive}
          resetState={() => {
            dispatch(resetIsDealSourceVehicle());
            dispatch(resetAddedIsUserVehicle());
          }}
          onHide={handleCloseDialog()}
          isDealSourceCar={isDealSourceVehicle}
          isMakeItWork={isMakeItWork}
        />
      )}
      {isShow && (
        <SetupRateSheetAndTaxRateModal
          show={isShow}
          onHide={() => {
            dispatch(closeRateSheetsModal());
            dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.REHASH_CALCULATOR }));
            dispatch(setIsModalsDisabled({ value: false, key: DisabledModals.UNLOCK_INVENTORY }));

            if (Object.keys(dealSourceData || {}).length > 0 && tier?.tier) {
              if (dealSourceData.vehicle_exists) {
                dispatch(setIsDealSourceVehicle(true));
                handleOpenDialog();
              } else if (!user?.isWithInventory && !user?.withWholesaleInventory) {
                setShowAddVehicleDialog(true);
              }

              sessionStorage.setItem(IS_SSO_AND_SHOW_LOAN_VARIABLES_DIALOG, 'true');
            }
          }}
        />
      )}
      <AddVehicleDialog
        tier={tier?.tier}
        show={showAddVehicleDialog}
        onHide={() => setShowAddVehicleDialog(false)}
        tableName={TableName.RAC}
        dealSourceData={dealSourceData}
        isMakeItWork={isMakeItWork}
      />
    </FormProvider>
  );
};
